import fastDiff from 'fast-diff';
import { isKarmaTestEnv } from '../env';
import { expandWhitespace } from '../textToHtml';
import { isText } from './node';
import { TextRange } from './range';

/**
 * Replaces a substring in the given text node with a replacement node.
 *
 * @param textNode
 *   The text node in which to replace the substring. This node will keep its place in the DOM and contain
 *   the text following the substring that was replaced, even if that text is empty.
 * @returns
 *   The offset in textNode following the substring to be replaced. If the splice succeeded, this will be 0.
 */
export function spliceTextNode(
  textNode: Text,
  offset: number,
  length: number,
  replacementNode: Node,
): number {
  const { ownerDocument: document, parentNode } = textNode;
  if (!document || !parentNode) {
    return offset + length;
  }

  const text = textNode.data;
  const textBeforeSplice = text.substring(0, offset);
  // eslint-disable-next-line no-param-reassign
  textNode.data = text.substring(offset + length);

  const newTextNode = textBeforeSplice.length > 0 ? document.createTextNode(textBeforeSplice) : undefined;
  if (newTextNode) {
    parentNode.insertBefore(newTextNode, textNode);
  }
  parentNode.insertBefore(replacementNode, textNode);
  return 0;
}

/** Applies text diffs to the text content of a DOM container, performing as few DOM mutations as necessary. */
export function patchTextContent(container: Node, diffs: readonly fastDiff.Diff[]): boolean {
  const document = container.ownerDocument;
  // istanbul ignore if
  if (!document) {
    return false;
  }

  const iterator = document.createNodeIterator(container, NodeFilter.SHOW_TEXT, {
    acceptNode: candidateNode =>
      isText(candidateNode) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
  });

  // To be safe, don't iterate more often than the number of diffs + the total length of all diffs.
  const maxIterationCount = diffs.length + diffs.reduce((length, diff) => length + diff[1].length, 0);
  let iterationCount = 0;

  // Index of the current diff, to track which diffs have been processed.
  let diffIndex = 0;
  // Offset in the current diff, to track how much of the current diff has been processed.
  let diffOffset = 0;
  // Current text node.
  let node: Text | null;
  while ((node = iterator.nextNode() as Text | null)) {
    // Offset in the current text node, to track how much of the current text node has been processed.
    let nodeOffset = 0;

    // Stay on the current text node if the next diff is an insert.
    while (
      diffIndex < diffs.length &&
      (diffs[diffIndex][0] === fastDiff.INSERT || nodeOffset < node.data.length)
    ) {
      // Current diff.
      const diff = diffs[diffIndex];
      // Total length of the current diff.
      const diffLength = diff[1].length;

      switch (diff[0]) {
        case fastDiff.EQUAL: {
          const remainingNodeLength = node.data.length - nodeOffset;
          const remainingDiffLength = diffLength - diffOffset;
          const intersectionLength = Math.min(remainingDiffLength, remainingNodeLength);
          const nodeText = node.data.substring(nodeOffset, nodeOffset + intersectionLength);
          const diffText = diff[1].substring(diffOffset, diffOffset + intersectionLength);

          // To be safe, assert the text is what we expect.
          if (nodeText !== diffText) {
            const message = `Expected "${diffText}" but found "${nodeText}" while applying text content diff`;
            // istanbul ignore else
            if (isKarmaTestEnv) {
              // Hard fail when running tests.
              throw new Error(message);
            } else {
              // eslint-disable-next-line no-console
              console.error(message);
              return false;
            }
          }

          nodeOffset += intersectionLength;
          diffOffset += intersectionLength;
          break;
        }
        case fastDiff.INSERT: {
          const textBefore = node.data.substring(0, nodeOffset);
          const textAfter = node.data.substring(nodeOffset);
          node.data = textBefore + expandWhitespace(diff[1]) + textAfter;

          // Note: the node length will be re-calculated on the next iteration.
          nodeOffset += diffLength;
          diffOffset += diffLength;
          break;
        }
        case fastDiff.DELETE: {
          const remainingNodeLength = node.data.length - nodeOffset;
          const remainingDiffLength = diffLength - diffOffset;
          const intersectionLength = Math.min(remainingDiffLength, remainingNodeLength);

          const textBefore = node.data.substring(0, nodeOffset);
          const textAfter = node.data.substring(nodeOffset + intersectionLength);
          node.data = textBefore + textAfter;

          diffOffset += intersectionLength;
          break;
        }
        // istanbul ignore next
        default:
          throw new Error(`Unexpected diff type "${diff[0]}"`);
      }

      // Proceed to the next diff if we've exhausted it.
      if (diffOffset === diffLength) {
        diffIndex += 1;
        diffOffset = 0;
      }

      // To be safe, protect against infinite loops.
      iterationCount += 1;
      // istanbul ignore next
      if (iterationCount > maxIterationCount) {
        if (isKarmaTestEnv) {
          // Hard fail when running tests.
          throw new Error('Infinite loop detected while applying diff to text content');
        } else {
          // eslint-disable-next-line no-console
          console.error('Infinite loop detected while applying diff to text content');
          return false;
        }
      }
    }
  }

  // Require all remaining diffs to be empty.
  return diffs.slice(diffIndex).every(diff => diff[1].length === 0);
}

/** Corrects a text range for the given text diffs. */
export function patchTextRange(textRange: TextRange, diffs: readonly fastDiff.Diff[]): TextRange {
  return {
    startIndex: patchTextIndex(textRange.startIndex, diffs),
    endIndex: patchTextIndex(textRange.endIndex, diffs),
  };
}

/** Corrects a text index for the given text diffs. */
export function patchTextIndex(textIndex: number, diffs: readonly fastDiff.Diff[]): number {
  let currentIndex = 0;
  let newTextIndex = textIndex;

  for (let diffIndex = 0; diffIndex < diffs.length && currentIndex < newTextIndex; diffIndex++) {
    const diff = diffs[diffIndex];

    switch (diff[0]) {
      case fastDiff.EQUAL:
        currentIndex += diff[1].length;
        break;

      case fastDiff.INSERT:
        currentIndex += diff[1].length;
        newTextIndex += diff[1].length;
        break;

      case fastDiff.DELETE:
        newTextIndex -= diff[1].length;
        break;

      default:
        break;
    }
  }

  return newTextIndex;
}
